import { useMemo, useState } from "react";
import {
  ChevronDown, ChevronUp, Copy, Sparkles, RotateCcw,
} from "lucide-react";
import { useToast } from "../Toast";

// Generador de URLs con UTMs + mini guía educativa.
// Renderiza un acordeón. Solo se muestra si hay un publicUrl válido.

const FIELDS = [
  {
    key: "utm_source",
    label: "Origen",
    placeholder: "instagram",
    hint: "De dónde viene el clic.",
    required: true,
  },
  {
    key: "utm_medium",
    label: "Medio",
    placeholder: "bio, cpc, email",
    hint: "Tipo de canal.",
    required: true,
  },
  {
    key: "utm_campaign",
    label: "Campaña",
    placeholder: "promo-verano",
    hint: "Acción concreta a la que pertenece.",
    required: true,
  },
  {
    key: "utm_term",
    label: "Término (opcional)",
    placeholder: "entrenador-personal",
    hint: "Útil para Google Ads o palabras clave.",
    required: false,
  },
  {
    key: "utm_content",
    label: "Contenido (opcional)",
    placeholder: "carrusel-1",
    hint: "Para diferenciar dos creatividades.",
    required: false,
  },
];

const PRESETS = [
  { id: "ig-bio", label: "Instagram (bio)", v: { utm_source: "instagram", utm_medium: "bio", utm_campaign: "perfil" } },
  { id: "ig-stories", label: "Instagram (stories)", v: { utm_source: "instagram", utm_medium: "stories", utm_campaign: "" } },
  { id: "wpp", label: "WhatsApp", v: { utm_source: "whatsapp", utm_medium: "chat", utm_campaign: "" } },
  { id: "email", label: "Email/Newsletter", v: { utm_source: "newsletter", utm_medium: "email", utm_campaign: "" } },
];

const EMPTY = {
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_term: "",
  utm_content: "",
};

function slugifyParam(v) {
  return v
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");
}

export default function LeadCaptureUtmBuilder({ baseUrl }) {
  const { showSuccess, showError } = useToast();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(EMPTY);

  const generatedUrl = useMemo(() => {
    if (!baseUrl) return "";
    const params = new URLSearchParams();
    for (const f of FIELDS) {
      const v = slugifyParam(values[f.key] || "");
      if (v) params.set(f.key, v);
    }
    const qs = params.toString();
    return qs ? `${baseUrl}?${qs}` : baseUrl;
  }, [baseUrl, values]);

  const requiredOk = FIELDS
    .filter((f) => f.required)
    .every((f) => slugifyParam(values[f.key] || "").length > 0);

  const handleCopy = async () => {
    if (!requiredOk) {
      showError("Rellena al menos Origen, Medio y Campaña");
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedUrl);
      showSuccess("Enlace copiado");
    } catch {
      showError("No se pudo copiar");
    }
  };

  const applyPreset = (preset) => {
    setValues({ ...EMPTY, ...preset.v });
  };

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center gap-2 text-gray-800 font-medium">
          <Sparkles size={15} className="text-emerald-600" />
          Crea enlaces con seguimiento (UTMs)
        </span>
        {open ? (
          <ChevronUp size={16} className="text-gray-400" />
        ) : (
          <ChevronDown size={16} className="text-gray-400" />
        )}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-gray-100 bg-gray-50/50">
          <p className="text-xs text-gray-600 leading-relaxed">
            Las UTMs son etiquetas que añades a tu enlace para saber{" "}
            <strong>de dónde viene cada lead</strong>. Por ejemplo, si compartes
            tu enlace en Instagram y en una newsletter, podrás distinguir cuál
            te trae más clientes. Las verás en el detalle de cada lead.
          </p>

          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                className="px-2.5 py-1 text-xs rounded-full bg-white border border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-200 hover:text-emerald-700 transition-colors"
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setValues(EMPTY)}
              className="px-2.5 py-1 text-xs rounded-full text-gray-500 hover:bg-gray-100 transition-colors flex items-center gap-1"
              title="Vaciar campos"
            >
              <RotateCcw size={11} />
              Vaciar
            </button>
          </div>

          <div className="space-y-2">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {f.label}
                  {f.required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
                <input
                  type="text"
                  value={values[f.key]}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [f.key]: e.target.value }))
                  }
                  placeholder={f.placeholder}
                  maxLength={80}
                  className="w-full px-2.5 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400"
                />
                <p className="mt-0.5 text-[11px] text-gray-500">{f.hint}</p>
              </div>
            ))}
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              Tu enlace con UTMs
            </p>
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs border ${
                requiredOk
                  ? "bg-white border-gray-200"
                  : "bg-gray-100 border-gray-200 text-gray-400"
              }`}
            >
              <span className="flex-1 truncate font-mono">
                {generatedUrl || "—"}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                disabled={!requiredOk}
                className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Copiar enlace"
              >
                <Copy size={13} className="text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
